import { REQUEST_MAX_ATTEMPTS, REQUEST_RETRY_DELAY } from "../config/settings";
import { secondsToMilliseconds } from "./date";
import logger from "./logger";

export function debounce<Args extends unknown[], R>(func: (...args: Args) => R, delay: number): (...args: Args) => void { 
  let timeoutId: ReturnType<typeof setTimeout>;

  return function(...args: Args): void {
    const context = null; //this // just to pass typescript error: "Unexpected alisaing of 'this' to local variable"
    
    clearTimeout(timeoutId);
    
    timeoutId = setTimeout(function() { func.apply(context, args); }, delay);
  };
}

export function debounceWithPromise<Args extends unknown[], R>(func: (...args: Args) => R, wait: number): (...args: Args) => Promise<Awaited<R>> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  
  // Track the single active resolve/reject for the latest call
  let activeResolve: ((value: Awaited<R> | PromiseLike<Awaited<R>>) => void) | undefined;
  let activeReject: ((reason?: unknown) => void) | undefined;

  return (...args: Args): Promise<Awaited<R>> => {
    if(timeoutId) {
      clearTimeout(timeoutId);
      // Reject previous pending promises because they were debounced/cancelled
      const error = { 
        name: "Pending Promise Cancelled", 
        message: "Pending Promise Resolution Cancelled by a newer debounced call" 
      };
      activeReject?.(error);
    }

    return new Promise<Awaited<R>>((resolve, reject) => {
      activeResolve = resolve;
      activeReject = reject;

      timeoutId = setTimeout(async () => {
        try {
          const result = await func(...args);
          activeResolve?.(result);
        } catch(error) {
          activeReject?.(error);
        }
      }, wait);
    });
  };
}

export function throttle(func: Function, throttleDuration: number) { // eslint-disable-line
  // While throttle is active, no function calls will be executed
  let throttleOn = false; 

  return function(...args) {
    const context = null; // this; // just to pass typescript error: "Unexpected alisaing of 'this' to local variable"

    // If the throttle is active when the function is invoked, do nothing...
    if(throttleOn) {
      return;
    }
    
    // ...Otherwise, execute the function invocation. 
    // However, before doing that, activate the throttle, so that no other calls are allowed.
    throttleOn = true;
    func.apply(context, args);

    // Even after the current invocation finishes executing, 
    // No function calls will be allowed until the throttle duration has elapsed.
    //
    // An alternative implementation would probably put this setTimeout call 
    // above the function invocation (func.apply(context, args))
    // But, depending on the context/situation, that could lead to race conditions 
    // if the timer expires before the current function is done executing.
    setTimeout(function() { throttleOn = false; }, throttleDuration);
  }
}

export async function sleep(ms: number) {
  return new Promise(res => setTimeout(res, ms));
}

type OptionArgs = {
  maxAttempts?: number;
  baseDelay?: number; // The back-off factor in seconds
}

// Deprectaed. Use the withExponentialBackoffRetry Higher-Order function instead.
export async function retryWithExponentialBackOff(callback: () => Promise<void>, options?: OptionArgs): Promise<void> {
  const { maxAttempts = REQUEST_MAX_ATTEMPTS, baseDelay = REQUEST_RETRY_DELAY } = options ?? {};

  let attempts = 0;
  const delayInMs = secondsToMilliseconds(baseDelay);
  
  while(attempts < maxAttempts) {
    try {
      await callback();
      return;
    } catch (err) {
      attempts++;
      logger.error(`Retrying (${attempts}/${maxAttempts}) - Error:`, err);
  
      if(attempts === maxAttempts) {
        logger.error(`${maxAttempts} exceeded.`);
      }
  
      // Exponential backoff
      await new Promise(res => setTimeout(res, delayInMs * attempts));
    }
  }
}

/**
 * Takes a function and returns the function wrapped in exponential backoff retries  
 */
export function withExponentialBackoffRetry<T>(fn: Function, options?: OptionArgs): (...args) => Promise<T> { // eslint-disable-line
  const { maxAttempts = REQUEST_MAX_ATTEMPTS, baseDelay = REQUEST_RETRY_DELAY } = options ?? {};

  let attempts = 0;
  const delayInMs = secondsToMilliseconds(baseDelay);
  
  return function(...args) {
    const context = null; // this; // just to pass typescript error: "Unexpected alisaing of 'this' to local variable"

    return new Promise((resolve, reject) => {
      (async function() {
        while(attempts < maxAttempts) {
          try {
            const val = await fn.apply(context, args);

            resolve(val);
            return; // Don't keep running the function after a successful response
          } catch (err) {
            attempts++;
            logger.error(`Retrying (${attempts}/${maxAttempts}) - Error:`, err);
        
            if(attempts === maxAttempts) {
              logger.error(`${maxAttempts} exceeded.`);
              reject(err);
            }
        
            // Exponential backoff (delay/sleep for some time before retrying)
            await new Promise(res => setTimeout(res, delayInMs * attempts));
          }
        }
      }());
    });
  }
}

/**
 * Debounce function to limit the rate of function calls
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 * 
 * (implemented by cursor AI)
 */
/*function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;

  return function executedFunction(...args: Parameters<T>) {
    function deferredFunction() {
      clearTimeout(timeout);
      func(...args);
    }

    clearTimeout(timeout);
    timeout = setTimeout(deferredFunction, wait);
  };
}*/
